import { Action, ManageAction, ResponseGridItem, Status } from './types';

export class ResponseConstants {
  public static EXECUTE = 2;
  public static MARK_AS_DONE = 8;
  public static DISMISS = 6;
  public static CANCEL = 10;
  public static RETRY = 11;

  public static readonly MARKABLE_AND_DISMISSIBLE_STATUSES: Array<Status> = [
    Status.EXTERNAL_ACTION_NEEDED,
    Status.TO_BE_EXECUTED,
    Status.EXECUTION_FAILED,
  ];

  public static readonly RESTORABLE_STATUSES: Array<Status> = [Status.DISMISSED, Status.DONE_EXTERNALLY];

  public static EXECUTABLE_ACTION_TYPES = [
    Action.ISOLATE,
    Action.DELETE_EMAIL_O365,
    Action.DISABLE_USER_O365,
    Action.DISABLE_USER_AD,
    Action.ENABLE_USER_O365,
    Action.ENABLE_USER_AD,
    Action.RESET_CREDENTIALS_AD,
    Action.RESTORE_ENDPOINT,
    Action.RESET_CREDENTIALS_O365,
    Action.RISKY_USER,
    Action.DEACTIVATE_USER_AWS,
    Action.DELETE_EMAIL_GOOGLE,
    Action.DISABLE_USER_GOOGLE,
    Action.DISABLE_USER_ATLASSIAN,
    Action.RESET_CREDENTIALS_GOOGLE,
  ];

  public static ERROR_CODE_PARTIAL_EXECUTION = 992;
  public static ERROR_CODE_RESPONSE_ACTIONS_DISABLED_FOR_SENSOR = 989;
  public static ERROR_CODE_NO_ENABLED_SENSORS = 997;

  public TOAST_MESSAGES_SUCCESSFUL: { [status: number]: Array<string> };

  public readonly MANAGE_ACTIONS: { [name: string]: ManageAction };

  constructor(translateFn: (x: string) => string) {
    this.TOAST_SUCCESS_MESSAGE = translateFn(' actions successfully executed');

    this.EXECUTE_AND_RETRY_MESSAGE_SINGULAR = translateFn('Response action initiated');
    this.EXECUTE_AND_RETRY_MESSAGE_PLURAL = translateFn(' response actions initiated');

    this.MARK_AS_DONE_MESSAGE_SINGULAR = translateFn('Response action marked as done');
    this.MARK_AS_DONE_MESSAGE_PLURAL = translateFn(' response actions marked as done');

    this.DISMISSED_MESSAGE_SINGULAR = translateFn('Response action dismissed');
    this.DISMISSED_MESSAGE_PLURAL = translateFn(' response actions dismissed');

    this.RESTORE_MESSAGE_SINGULAR = translateFn('Response action restored');
    this.RESTORE_MESSAGE_PLURAL = translateFn(' response actions restored');

    this.CANCEL_MESSAGE_SINGULAR = translateFn('Response action canceled');

    // failed
    this.EXECUTE_AND_RETRY_FAILED_MESSAGE_SINGULAR = translateFn('Response action failed to initiate');
    this.EXECUTE_AND_RETRY_FAILED_MESSAGE_PLURAL = translateFn(' response actions failed to initiate');
    this.RESPONSE_ACTIONS_DISABLED_FOR_SENSOR_FAILED_MESSAGE_SINGULAR = translateFn('Request failed. You need to enable response actions on the associated sensor.');
    this.RESPONSE_ACTIONS_DISABLED_FOR_SENSOR_FAILED_MESSAGE_PLURAL = translateFn('Request failed. You need to enable response actions on the associated sensors.');
    this.RESPONSE_ACTIONS_NO_SENSORS = translateFn('No sensors have been enabled for this response type.');
    this.GENERAL_FAILED_MESSAGE = translateFn('Status change failed');

    // singular because it s used like this in phrases.json
    this.ENUMERATION = 'enumeration';

    this.TOAST_MESSAGES_SUCCESSFUL = {
      // execute / retry
      [Status.PENDING]: [this.EXECUTE_AND_RETRY_MESSAGE_SINGULAR, this.EXECUTE_AND_RETRY_MESSAGE_PLURAL],
      // mark as done
      [Status.DONE_EXTERNALLY]: [this.MARK_AS_DONE_MESSAGE_SINGULAR, this.MARK_AS_DONE_MESSAGE_PLURAL],
      // dismiss
      [Status.DISMISSED]: [this.DISMISSED_MESSAGE_SINGULAR, this.DISMISSED_MESSAGE_PLURAL],
      // restore v[0], v[1] and cancel v[2]
      [Status.TO_BE_EXECUTED]: [
        this.RESTORE_MESSAGE_SINGULAR,
        this.RESTORE_MESSAGE_PLURAL,
        this.CANCEL_MESSAGE_SINGULAR,
      ],
      [Status.EXTERNAL_ACTION_NEEDED]: [this.RESTORE_MESSAGE_SINGULAR, this.RESTORE_MESSAGE_PLURAL],
    };

    this.MANAGE_ACTIONS = {
      markActionAsDone: {
        title: translateFn('Mark action as done'),
        value: Status.DONE_EXTERNALLY,
        canBeDone: (item: ResponseGridItem) =>
          ResponseConstants.MARKABLE_AND_DISMISSIBLE_STATUSES.includes(item.status),
      },
      dismissAction: {
        title: translateFn('Dismiss action'),
        value: Status.DISMISSED,
        canBeDone: (item: ResponseGridItem) =>
          ResponseConstants.MARKABLE_AND_DISMISSIBLE_STATUSES.includes(item.status),
      },
      retryFailedAction: {
        title: translateFn('Retry failed action'),
        value: Status.PENDING,
        canBeDone: (item: ResponseGridItem) =>
          ResponseConstants.EXECUTABLE_ACTION_TYPES.includes(item.type) && item.status === Status.EXECUTION_FAILED,
      },
      restoreDismissedAction: {
        title: translateFn('Restore action'),
        // The status value should be Status.TO_BE_EXECUTED for executable types and EXTERNAL_ACTION_NEEDED = 7 otherwise
        // but I need to make 2 back-end requests for different types, therefore, return the default Status.TO_BE_EXECUTED and
        // filter the executable types in the controller logic while calling rpc
        value: Status.TO_BE_EXECUTED,
        canBeDone: (item: ResponseGridItem) => ResponseConstants.RESTORABLE_STATUSES.includes(item.status),
      },
    };
  }

  public TOAST_SUCCESS_MESSAGE: string;

  // success
  public EXECUTE_AND_RETRY_MESSAGE_SINGULAR: string;
  public EXECUTE_AND_RETRY_MESSAGE_PLURAL: string;

  public MARK_AS_DONE_MESSAGE_SINGULAR: string;
  public MARK_AS_DONE_MESSAGE_PLURAL: string;

  public DISMISSED_MESSAGE_SINGULAR: string;
  public DISMISSED_MESSAGE_PLURAL: string;

  public RESTORE_MESSAGE_SINGULAR: string;
  public RESTORE_MESSAGE_PLURAL: string;

  public CANCEL_MESSAGE_SINGULAR: string;

  // failed
  public EXECUTE_AND_RETRY_FAILED_MESSAGE_SINGULAR: string;
  public EXECUTE_AND_RETRY_FAILED_MESSAGE_PLURAL: string;
  public RESPONSE_ACTIONS_DISABLED_FOR_SENSOR_FAILED_MESSAGE_SINGULAR: string;
  public RESPONSE_ACTIONS_DISABLED_FOR_SENSOR_FAILED_MESSAGE_PLURAL: string;
  public GENERAL_FAILED_MESSAGE: string;
  public RESPONSE_ACTIONS_NO_SENSORS: string;

  // singular because it s used like this in phrases.json
  public ENUMERATION: string;

}
